/* eslint-disable react/no-unescaped-entities */
/**
 * OwnerDesk V1 · Auth pages (signup + login).
 *
 * Owner is a lightweight role: email + password, no Stripe, no referral.
 * Login reuses `/api/auth/login` (already unified). Signup uses the
 * dedicated `/api/auth/owner/signup` endpoint which also performs the
 * seamless anon-to-owner linkage on the server side.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export function OwnerSignupPage() {
  const { signupOwner } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", password: "", password2: "",
  });
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = form.nome && form.cognome && form.email
    && form.password.length >= 6 && form.password === form.password2;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      const r = await signupOwner({
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        email: form.email.trim(),
        password: form.password,
      });
      const linked = r?.anon_linkage;
      if (linked && (linked.cases + linked.journeys) > 0) {
        toast.success("Account creato — la tua analisi è ora salvata.");
      } else {
        toast.success("Account creato. Benvenuto in ValutaGenio.");
      }
      nav("/proprietari/dashboard");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (Array.isArray(d) && d[0]?.msg) ? d[0].msg
        : "Errore registrazione. Verifica i dati e riprova.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Crea il tuo account proprietario"
      subtitle="Salva le tue analisi, riprendile da qualsiasi dispositivo, resta in contatto con il tuo Property Advisor."
    >
      <form onSubmit={submit} className="space-y-4" data-testid="owner-signup-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome">
            <input className="vg-input" required value={form.nome}
                   onChange={(e) => upd("nome", e.target.value)}
                   data-testid="owner-signup-nome" />
          </Field>
          <Field label="Cognome">
            <input className="vg-input" required value={form.cognome}
                   onChange={(e) => upd("cognome", e.target.value)}
                   data-testid="owner-signup-cognome" />
          </Field>
        </div>
        <Field label="Email">
          <input className="vg-input" type="email" required value={form.email}
                 onChange={(e) => upd("email", e.target.value)}
                 data-testid="owner-signup-email" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Password">
            <input className="vg-input" type="password" required minLength={6}
                   value={form.password}
                   onChange={(e) => upd("password", e.target.value)}
                   data-testid="owner-signup-password" />
          </Field>
          <Field label="Conferma password">
            <input className="vg-input" type="password" required minLength={6}
                   value={form.password2}
                   onChange={(e) => upd("password2", e.target.value)}
                   data-testid="owner-signup-password2" />
          </Field>
        </div>
        <button className="vg-btn-primary w-full"
                disabled={!canSubmit || loading}
                data-testid="owner-signup-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Crea account"}
        </button>
        <p className="text-sm text-center text-gray-500 mt-4">
          Hai già un account? <Link to="/proprietari/login"
            className="text-[#B40101] font-semibold"
            data-testid="owner-goto-login">Accedi</Link>
        </p>
        <p className="text-xs text-center text-gray-400 mt-2">
          Sei un Property Advisor? <Link to="/signup" className="hover:underline">Registrati qui →</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function OwnerLoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Bentornato ${u.nome}!`);
      const dest = u.ruolo === "owner" ? "/proprietari/dashboard"
        : u.ruolo === "admin" ? "/admin"
        : "/agent";
      nav(dest);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non corrette");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Accedi alla tua area proprietario" subtitle="Riprendi le tue analisi da qui.">
      <form onSubmit={submit} className="space-y-4" data-testid="owner-login-form">
        <Field label="Email">
          <input className="vg-input" type="email" required value={email}
                 onChange={(e) => setEmail(e.target.value)}
                 data-testid="owner-login-email" />
        </Field>
        <Field label="Password">
          <input className="vg-input" type="password" required value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 data-testid="owner-login-password" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={remember}
                 onChange={(e) => setRemember(e.target.checked)}
                 data-testid="owner-login-remember" />
          Ricordami su questo dispositivo
        </label>
        <button className="vg-btn-primary w-full" disabled={loading}
                data-testid="owner-login-submit">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Accedi"}
        </button>
      </form>
      <div className="flex justify-between text-sm mt-6 text-gray-500">
        <Link to="/forgot-password" className="hover:text-[#B40101]"
              data-testid="owner-forgot-link">Password dimenticata?</Link>
        <Link to="/proprietari/signup" className="hover:text-[#B40101]"
              data-testid="owner-goto-signup">Registrati</Link>
      </div>
    </AuthShell>
  );
}

// ---------- Shared bits (kept local to avoid coupling with agent Auth.jsx) --

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0A0A0A] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="vg-funnel-bg flex items-center justify-center p-4 min-h-screen">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/"><Logo size={28} /></Link>
        </div>
        <div className="vg-card p-8">
          <h1 className="text-2xl font-bold text-[#0A0A0A]">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
